import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert";
import {
  Trash2,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Mail,
  User,
  Calendar,
  MessageSquare,
} from "lucide-react";
import { DOMAIN } from "../strings/CONSTANTS";

interface DeleteRequest {
  _id: string;
  email: string;
  name: string;
  reason: string;
  feedback: string;
  status: "pending" | "processed" | "rejected";
  ipAddress: string;
  userAgent: string;
  createdAt: string;
  processedAt?: string;
}

const DeleteRequests: React.FC = () => {
  const [requests, setRequests] = useState<DeleteRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<DeleteRequest | null>(
    null
  );
  const [filter, setFilter] = useState<
    "all" | "pending" | "processed" | "rejected"
  >("all");

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${DOMAIN}/admin/delete-requests`);
      if (response.data.success) {
        setRequests(response.data.requests);
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      Swal("Error", "Failed to fetch delete requests", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const updateStatus = async (id: string, status: "processed" | "rejected") => {
    const action = status === "processed" ? "mark as processed" : "reject";

    const result = await Swal({
      title: `Are you sure?`,
      text: `Do you want to ${action} this deletion request?`,
      icon: "warning",
      buttons: ["Cancel", "Yes"],
      dangerMode: true,
    });

    if (result) {
      try {
        const response = await axios.put(
          `${DOMAIN}/admin/delete-request/${id}`,
          { status }
        );
        if (response.data.success) {
          Swal(
            "Success",
            `Request ${
              status === "processed" ? "marked as processed" : "rejected"
            }`,
            "success"
          );
          fetchRequests();
        }
      } catch (error) {
        Swal("Error", "Failed to update status", "error");
      }
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case "processed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="w-3 h-3 mr-1" />
            Processed
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const filteredRequests =
    filter === "all" ? requests : requests.filter((r) => r.status === filter);

  const getReasonColor = (reason: string) => {
    const colors: Record<string, string> = {
      "Privacy concerns": "bg-blue-100 text-blue-800",
      "No longer using the app": "bg-gray-100 text-gray-800",
      "Found a better alternative": "bg-purple-100 text-purple-800",
      "Too many notifications": "bg-orange-100 text-orange-800",
      "Technical issues": "bg-red-100 text-red-800",
      Other: "bg-pink-100 text-pink-800",
    };
    return colors[reason] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading delete requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Delete Account Requests
          </h1>
          <p className="text-gray-600">
            Manage and process user account deletion requests
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter((r) => r.status === "pending").length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-yellow-500 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Processed</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter((r) => r.status === "processed").length}
                </p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Rejected</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter((r) => r.status === "rejected").length}
                </p>
              </div>
              <XCircle className="w-10 h-10 text-red-500 opacity-50" />
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.length}
                </p>
              </div>
              <Trash2 className="w-10 h-10 text-blue-500 opacity-50" />
            </div>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="mb-6 flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "all"
                ? "bg-indigo-600 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "pending"
                ? "bg-yellow-500 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter("processed")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "processed"
                ? "bg-green-500 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Processed
          </button>
          <button
            onClick={() => setFilter("rejected")}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              filter === "rejected"
                ? "bg-red-500 text-white"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Rejected
          </button>
        </div>

        {/* Requests Table */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No delete requests found
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((request) => (
                    <tr
                      key={request._id}
                      className="hover:bg-gray-50 transition"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <Mail className="w-5 h-5 text-indigo-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {request.email}
                            </div>
                            <div className="text-sm text-gray-500">
                              {request.name || "No name provided"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs rounded-full ${getReasonColor(
                            request.reason
                          )}`}
                        >
                          {request.reason}
                        </span>
                        {request.feedback && (
                          <div className="text-xs text-gray-500 mt-1 truncate max-w-xs">
                            "{request.feedback.substring(0, 50)}"
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(request.status)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {new Date(request.createdAt).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {new Date(request.createdAt).toLocaleTimeString()}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => setSelectedRequest(request)}
                            className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition"
                          >
                            <Eye className="w-4 h-4 inline mr-1" />
                            View
                          </button>
                          {request.status === "pending" && (
                            <>
                              <button
                                onClick={() =>
                                  updateStatus(request._id, "processed")
                                }
                                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition"
                              >
                                <CheckCircle className="w-4 h-4 inline mr-1" />
                                Process
                              </button>
                              <button
                                onClick={() =>
                                  updateStatus(request._id, "rejected")
                                }
                                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition"
                              >
                                <XCircle className="w-4 h-4 inline mr-1" />
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Details Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900">
                    Request Details
                  </h2>
                  <button
                    onClick={() => setSelectedRequest(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Email
                    </label>
                    <p className="mt-1 text-gray-900">
                      {selectedRequest.email}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Name
                    </label>
                    <p className="mt-1 text-gray-900">
                      {selectedRequest.name || "Not provided"}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Status
                    </label>
                    <div className="mt-1">
                      {getStatusBadge(selectedRequest.status)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Submitted
                    </label>
                    <p className="mt-1 text-gray-900">
                      {new Date(selectedRequest.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-500">
                      Reason
                    </label>
                    <p className="mt-1 text-gray-900">
                      {selectedRequest.reason}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-500">
                      Feedback
                    </label>
                    <p className="mt-1 text-gray-900">
                      {selectedRequest.feedback || "No feedback provided"}
                    </p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-500">
                      IP Address
                    </label>
                    <p className="mt-1 text-gray-900">
                      {selectedRequest.ipAddress || "Not recorded"}
                    </p>
                  </div>
                  {selectedRequest.processedAt && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-500">
                        Processed At
                      </label>
                      <p className="mt-1 text-gray-900">
                        {new Date(selectedRequest.processedAt).toLocaleString()}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeleteRequests;
